import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { orders as ordersApi, getImageUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Package, MapPin, FileText, Download, ShieldCheck } from 'lucide-react';

const OrderDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await ordersApi.getById(id);
        setOrder(response.data);
      } catch (error) {
        console.error('Failed to fetch order:', error);
        toast.error('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const loadOrder = async () => {
    try {
      const response = await ordersApi.getById(id);
      setOrder(response.data);
    } catch (error) {
      console.error('Failed to load order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdating(true);
    try {
      await ordersApi.updateStatus(id, newStatus);
      setOrder(prev => ({ ...prev, status: newStatus }));
      toast.success('Order status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      processing: 'bg-purple-100 text-purple-800 border-purple-200',
      shipped: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Size mapping functions removed - now handled by backend

  // Download order for POS import - uses backend endpoint to fetch barcodes
  const handleDownloadForPOS = async () => {
    if (!order) {
      toast.error('No order data available');
      return;
    }

    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      const downloadUrl = ordersApi.downloadPOS(order.id);
      
      // Fetch with auth header
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `order_${order.order_number}_pos.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Order data downloaded for POS import');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download order data');
    }
  };

  // Download order as PDF
  const handleDownloadPDF = async () => {
    if (!order) {
      toast.error('No order data available');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const downloadUrl = ordersApi.downloadPDF(order.id);
      
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Order_${order.order_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Order PDF downloaded');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="container-wide py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[#F1F5F9] w-1/4" />
          <div className="h-64 bg-[#F1F5F9]" />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container-wide py-16 text-center">
        <p className="text-[#64748B]">Order not found</p>
        <Link to="/orders">
          <Button className="mt-4">Back to Orders</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container-wide py-8 md:py-12 fade-in">
      <Link
        to="/orders"
        className="inline-flex items-center text-sm text-[#64748B] hover:text-[#0F172A] mb-8"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Orders
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <p className="font-mono text-sm text-[#64748B]" data-testid="order-number">
            {order.order_number}
          </p>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A]">Order Details</h1>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={handleDownloadPDF}
            className="gap-2"
            data-testid="download-pdf-btn"
          >
            <FileText className="w-4 h-4" />
            Download PDF
          </Button>
          <Button
            variant="outline"
            onClick={handleDownloadForPOS}
            className="gap-2"
            data-testid="download-pos-btn"
          >
            <Download className="w-4 h-4" />
            Download for POS
          </Button>
          {isAdmin ? (
            <Select
              value={order.status}
              onValueChange={handleStatusUpdate}
              disabled={updating}
            >
              <SelectTrigger className="w-[180px]" data-testid="status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <Badge className={`${getStatusColor(order.status)} border text-sm px-3 py-1`}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Order Items */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-[#E2E8F0] p-6">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-6 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Order Items
              {order.has_backorder && (
                <span className="ml-2 text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded">
                  Contains Backorders
                </span>
              )}
            </h2>
            
            <div className="space-y-6">
              {/* Group items by category */}
              {Object.entries(
                order.items.reduce((acc, item) => {
                  const category = item.product_category || 'uncategorized';
                  if (!acc[category]) acc[category] = [];
                  acc[category].push(item);
                  return acc;
                }, {})
              ).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
                <div key={category}>
                  <p className="text-sm font-semibold text-[#002FA7] uppercase tracking-wide mb-3 capitalize border-b border-[#E2E8F0] pb-2">
                    {category.replace(/-/g, ' ')}
                  </p>
                  <div className="space-y-4">
                    {items.map((item, idx) => (
                      <div key={idx} className="border-b border-[#E2E8F0] pb-4 last:border-0 last:pb-0">
                        <div className="flex gap-4">
                          {/* Product Image */}
                          <div className="w-16 h-16 bg-[#F8FAFC] rounded overflow-hidden flex-shrink-0">
                            <img
                              src={getImageUrl(item.product_image) || 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400'}
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=400';
                              }}
                            />
                          </div>
                          {/* Product Details */}
                          <div className="flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-semibold text-[#0F172A]">{item.product_name}</p>
                                <p className="text-sm text-[#64748B] font-mono">{item.product_sku}</p>
                                {item.has_backorder && (
                                  <span className="inline-block text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded mt-1">
                                    Backorder
                                  </span>
                                )}
                              </div>
                              <span className="font-semibold text-[#0F172A]">
                                ${item.subtotal.toFixed(2)}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {Object.entries(item.sizes).map(([size, qty]) => {
                                const backorderInfo = item.backorder_info?.[size];
                                return (
                                  <span 
                                    key={size} 
                                    className={`text-xs px-2 py-1 ${backorderInfo ? 'bg-amber-50 border border-amber-200' : 'bg-[#F8FAFC]'}`}
                                  >
                                    {size}: {qty}
                                    {backorderInfo && (
                                      <span className="ml-1 text-amber-600">
                                        (ETA: {new Date(backorderInfo.eta).toLocaleDateString()})
                                      </span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                            <p className="text-sm text-[#64748B] mt-2">
                              {item.total_quantity} units × ${item.price.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {order.notes && (
            <div className="border border-[#E2E8F0] p-6">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Order Notes
              </h2>
              <p className="text-[#64748B]">{order.notes}</p>
            </div>
          )}
        </div>

        {/* Summary Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Order Summary */}
          <div className="border border-[#E2E8F0] p-6">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-4">Summary</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[#64748B]">Order Date</span>
                <span className="text-[#0F172A]">
                  {new Date(order.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#64748B]">Total Items</span>
                <span className="text-[#0F172A]">{order.items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#64748B]">Total Units</span>
                <span className="text-[#0F172A]">
                  {order.items.reduce((acc, item) => acc + item.total_quantity, 0)}
                </span>
              </div>
              <div className="border-t border-[#E2E8F0] pt-3 mt-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Subtotal</span>
                  <span className="text-[#0F172A]">
                    ${(order.subtotal || order.total).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">GST (10%)</span>
                  <span className="text-[#0F172A]">
                    ${(order.gst || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-[#E2E8F0]">
                  <span className="font-semibold text-[#0F172A]">Total (inc GST)</span>
                  <span className="text-2xl font-bold text-[#002FA7]" data-testid="order-total">
                    ${order.total.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="border border-[#E2E8F0] p-6">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Shipping Address
            </h2>
            <div className="text-sm text-[#64748B] space-y-1">
              <p>{order.shipping_address.street}</p>
              <p>
                {order.shipping_address.city}, {order.shipping_address.state}{' '}
                {order.shipping_address.postal_code}
              </p>
              <p>{order.shipping_address.country}</p>
            </div>
          </div>

          {/* Customer Info (Admin only) */}
          {isAdmin && (
            <div className="border border-[#E2E8F0] p-6">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-4">Customer</h2>
              <div className="text-sm space-y-1">
                <p className="font-medium text-[#0F172A]">{order.company_name}</p>
                <p className="text-[#64748B]">{order.user_email}</p>
              </div>
            </div>
          )}

          {/* T&C Acceptance Audit Trail */}
          {order.terms_accepted && (
            <div className="border border-[#E2E8F0] p-6 bg-[#F8FAFC]" data-testid="tc-audit-trail">
              <h2 className="text-lg font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-green-600" />
                Terms &amp; Conditions Accepted
              </h2>
              <div className="text-sm space-y-1.5 text-[#475569]">
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Accepted by</span>
                  <span className="font-medium text-[#0F172A]">
                    {order.terms_accepted_by_name || order.company_name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Timestamp</span>
                  <span className="text-[#0F172A]">
                    {order.terms_accepted_at ? new Date(order.terms_accepted_at).toLocaleString('en-AU', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', timeZoneName: 'short'
                    }) : '—'}
                  </span>
                </div>
                {isAdmin && order.terms_accepted_ip && (
                  <div className="flex justify-between">
                    <span className="text-[#64748B]">IP address</span>
                    <span className="font-mono text-xs text-[#0F172A]">{order.terms_accepted_ip}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailPage;
